import {
  Slider as CompoundSlider,
  Rail,
  Handles,
  SliderItem,
  GetHandleProps,
  Tracks,
  Ticks,
} from "react-compound-slider"
import { GetTrackProps } from "react-compound-slider/dist/types/Tracks/types"
import { Div } from "../div/Div"
import { HANDLE_SIZE, RAIL_HEIGHT, SHandle, SRail, STick, STrack, TICK_SIZE } from "./Slider.styled"

interface HandleProps {
  handle: SliderItem
  getHandleProps: GetHandleProps
}

const Handle = ({ handle, getHandleProps }: HandleProps) => {
  const { id, percent } = handle
  return (
    <SHandle
      css={{ left: `calc(${percent}% - ${HANDLE_SIZE / 2}px)`, top: -(RAIL_HEIGHT / 2) }}
      {...getHandleProps(id)}
    />
  )
}

interface TrackProps {
  id: string
  source: SliderItem
  target: SliderItem
  getTrackProps: GetTrackProps
}

const Track = ({ source, target, getTrackProps }: TrackProps) => {
  return (
    <STrack {...getTrackProps()} css={{ left: `${source.percent}%`, width: `${target.percent - source.percent}%` }} />
  )
}

interface TickProps {
  tick: SliderItem
  onClick?: (value: number) => void
}

const Tick = ({ tick, onClick }: TickProps) => {
  const { percent, value } = tick
  const left = percent == 0 || percent == 100 ? `${percent}%` : `calc(${percent}% - ${TICK_SIZE / 2}px)`
  return <STick css={{ left }} onClick={() => onClick?.(value)} />
}

interface Props {
  min: number
  max: number
  step?: number
  showLeftTrack?: boolean
  values: number[]
  tickValues?: number[]
  onValuesChange?: (values: readonly number[]) => void
}

const Slider = ({ min, max, step, showLeftTrack, tickValues, values, onValuesChange }: Props) => {
  return (
    <CompoundSlider
      rootStyle={{ position: "relative", width: "100%" }}
      domain={[min, max]}
      values={values}
      step={step}
      onUpdate={onValuesChange}
    >
      <Rail>{({ getRailProps }) => <SRail {...getRailProps()} />}</Rail>
      {tickValues && (
        <Ticks values={tickValues}>
          {({ ticks }) => (
            <Div className="slider-ticks">
              {ticks.map((tick) => (
                <Tick
                  key={tick.id}
                  tick={tick}
                  onClick={values.length === 1 ? (value) => onValuesChange?.([value]) : undefined}
                />
              ))}
            </Div>
          )}
        </Ticks>
      )}
      <Handles>
        {({ handles, getHandleProps }) => (
          <Div className="slider-handles">
            {handles.map((handle) => (
              <Handle key={handle.id} handle={handle} getHandleProps={getHandleProps} />
            ))}
          </Div>
        )}
      </Handles>
      {showLeftTrack && (
        <Tracks right={false}>
          {({ tracks, getTrackProps }) => (
            <Div className="slider-tracks">
              {tracks.map((track) => (
                <Track key={track.id} {...track} getTrackProps={getTrackProps} />
              ))}
            </Div>
          )}
        </Tracks>
      )}
    </CompoundSlider>
  )
}

export default Slider
